import './styles/graphic.scss';
import * as d3 from 'd3';
import { ckmeans } from 'simple-statistics';

const stateAbbrev = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  'District of Columbia': 'DC',
};

// Helper function to determine if a color is light or dark
function isLightColor(hexColor) {
  const rgb = hexColor.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!rgb) return false;
  const r = parseInt(rgb[1], 16);
  const g = parseInt(rgb[2], 16);
  const b = parseInt(rgb[3], 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

// Helper function to round break values to "nice" numbers for display
function roundToNice(value) {
  if (value <= 0) return 0;
  if (value === 1) return 1;

  // Define nice number sequences
  const niceNumbers = [1, 2, 5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 250, 300, 400, 500, 750, 1000];

  // Find the closest nice number
  let closest = niceNumbers[0];
  let minDiff = Math.abs(value - closest);

  for (const nice of niceNumbers) {
    const diff = Math.abs(value - nice);
    if (diff < minDiff) {
      minDiff = diff;
      closest = nice;
    }
  }

  return closest;
}

export default function (props, container, isMobile) {
  const {
    initialWidth, margins, data, width,
  } = props;

  // Cell size based on viewport
  const cell = isMobile ? 25 : 45;

  // Compute min/max x and y for proper centering
  const minX = d3.min(data, d => d.x);
  const maxX = d3.max(data, d => d.x);
  const minY = d3.min(data, d => d.y);
  const maxY = d3.max(data, d => d.y);

  // Total grid size
  const gridWidth = (maxX - minX + 1) * cell;
  const gridHeight = (maxY - minY + 1) * cell;

  // Set **dynamic** height instead of using `initialHeight`
  const height = gridHeight + margins.top + margins.bottom;

  // Compute offsets for centering within the full SVG width
  const OFFSET_X = (initialWidth - gridWidth) / 2;
  const OFFSET_Y = margins.top;

  // Transform function to properly center grid
  const translate = (x, y) => [
    (x - minX) * cell + OFFSET_X,
    (y - minY) * cell + OFFSET_Y,
  ];

  function renderLegend(svg, colorScale, breaks, displayBreaks, maxValue, legendMargin = 20) {
    const legendWidth = isMobile ? 220 : 250;
    const legendHeight = 15;
    const legendX = (initialWidth - legendWidth) / 2;
    const legendY = legendMargin;

    const legendG = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendX},${legendY})`);

    // Add title above legend
    legendG.append('text')
      .attr('class', 'legend-title')
      .attr('x', legendWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Number of measles cases');

    // Get the color range (excluding 'nocases')
    const colors = ['#E0D4EB', '#C7B2DA', '#AE8FCA', '#8C63B5', '#623E82'];
    const numBins = colors.length;

    // Draw color rectangles
    const rectWidth = legendWidth / numBins;
    colors.forEach((color, i) => {
      legendG.append('rect')
        .attr('x', i * rectWidth)
        .attr('y', 0)
        .attr('width', rectWidth)
        .attr('height', legendHeight)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5);
    });

    // Add labels below - use display breaks (rounded nice numbers)
    // Position them at the start of each color bar
    if (displayBreaks && displayBreaks.length > 0) {
      displayBreaks.forEach((breakValue, i) => {
        legendG.append('text')
          .attr('class', 'legend-label')
          .attr('x', i * rectWidth)
          .attr('y', legendHeight + 15)
          .attr('text-anchor', 'middle')
          .style('font-size', '12px')
          .style('fill', '#666')
          .text(d3.format(',')(breakValue));
      });

      // Add final label at the end only if it's different from the last break
      const lastBreak = displayBreaks[displayBreaks.length - 1];
      const displayMax = roundToNice(maxValue);
      if (maxValue && displayMax !== lastBreak) {
        legendG.append('text')
          .attr('class', 'legend-label')
          .attr('x', legendWidth)
          .attr('y', legendHeight + 15)
          .attr('text-anchor', 'middle')
          .style('font-size', '12px')
          .style('fill', '#666')
          .text(d3.format(',')(displayMax));
      }
    }
  }

  function renderGrid(updatedData, colorScale, breaks, displayBreaks, maxValue) {
    container.html('');

    // Tooltip div (one per render)
    let tooltip = d3.select('body').select('.state-tooltip');
    if (tooltip.empty()) {
      tooltip = d3.select('body').append('div')
        .attr('class', 'state-tooltip')
        .style('position', 'absolute')
        .style('pointer-events', 'none')
        .style('background', 'rgba(255, 255, 255, 0.85)')
        .style('color', '#000')
        .style('padding', '8px 12px')
        .style('border-radius', '6px')
        .style('font-size', '12px')
        .style('z-index', 1)
        .style('display', 'none');
    }

    // Add space for legend (title + bar + labels + padding)
    const legendHeight = 65;
    const totalHeight = height + legendHeight;

    // Update the SVG height dynamically based on the **actual grid**
    const svg = container.append('svg')
      .attr('width', initialWidth)
      .attr('height', totalHeight); // Make SVG fit the actual grid + legend

    // Render legend with adjusted margin (farther from dek, closer to map)
    if (breaks && breaks.length > 0) {
      renderLegend(svg, colorScale, breaks, displayBreaks, maxValue, 25);
    }

    const g = svg.append('g')
      .attr('transform', `translate(0,${margins.top + legendHeight})`);

    const countryG = g.append('g')
      .attr('class', 'country');

    // Bind states
    const state = countryG.selectAll('.state')
      .data(updatedData)
      .enter()
      .append('g')
      .attr('class', d => `state ${d.count > 0 ? 'cases' : 'nocases'}`)
      .attr('transform', d => `translate(${translate(d.x, d.y)})`);

    state.append('rect')
      .attr('x', -cell / 2)
      .attr('y', -cell / 2)
      .attr('width', cell - 1)
      .attr('height', cell - 1)
      .attr('fill', d => d.color || colorScale(d.count))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('class', 'state-rect')
      .on('mouseover touchstart', function (event, d) {
        // Show tooltip
        tooltip.style('display', 'block')
          .html(`<b>${d.state_name || d.key}</b><br><b>Cases:</b> ${typeof d.count !== 'undefined' ? d.count : 'N/A'}`);
        d3.select(this);
      })
      .on('mousemove touchmove', (event) => {
        // Position tooltip
        const [x, y] = d3.pointer(event, document.body);
        const tooltipNode = tooltip.node();
        if (!tooltipNode) return;
        const tooltipRect = tooltipNode.getBoundingClientRect();
        const winWidth = window.innerWidth;
        const winHeight = window.innerHeight;
        let left = x + 15;
        let top = y + 10;
        // Adjust if overflowing right
        if (left + tooltipRect.width > winWidth) {
          left = winWidth - tooltipRect.width - 10;
        }
        // Adjust if overflowing bottom
        if (top + tooltipRect.height > winHeight) {
          top = winHeight - tooltipRect.height - 10;
        }
        tooltip.style('left', `${Math.max(left, 0)}px`).style('top', `${Math.max(top, 0)}px`);
      })
      .on('mouseleave touchend', function () {
        tooltip.style('display', 'none');
        d3.select(this).attr('stroke', '#fff').attr('stroke-width', 1);
      });

    state.append('text')
      .attr('dy', '.35em')
      .attr('aria-label', d => d.category)
      .attr('fill', (d) => {
        const bgColor = d.color || colorScale(d.count);
        // Two darkest purples should have white text
        const darkPurples = ['#8C63B5', '#623E82'];
        if (darkPurples.includes(bgColor.toUpperCase())) {
          return '#ffffff';
        }
        return isLightColor(bgColor) ? '#262626' : '#ffffff';
      })
      .text(d => d.key);
  }

  // Load both JSON files in parallel
  Promise.all([
    d3.json('https://ix.cnn.io/data/measles/toplines_measles_cases.json'),
    d3.json('https://ix.cnn.io/data/measles/state_measles_cases.json'),
  ]).then(([toplineData, measlesData]) => {
    // Process toplines data
    if (!toplineData || toplineData.length === 0) {
      console.error('No data found in toplines JSON');
      return;
    }

    const toplinesData = toplineData[0];
    const {
      summary,
      last_updated_str,
      updated_date,
      total_cases_this_year,
      states_with_cases,
      unique_states_str,
    } = toplinesData;

    // Process state data
    const currentYear = new Date().getFullYear();
    const filteredMeasles = measlesData.filter(d => parseInt(d.year, 10) === currentYear)
      .map(d => ({
        ...d,
        abbrev: stateAbbrev[d.state_name] || null,
        category: d.has_case ? 'cases' : 'nocases',
      }));

    // Find the state with the most cases
    const statesWithCasesData = filteredMeasles.filter(d => d.count > 0);

    // states_with_cases counts states only; DC is tracked separately in the state data
    const dcHasCases = filteredMeasles.some(d => d.state_name === 'District of Columbia' && d.count > 0);
    const dcSuffix = dcHasCases ? ' and the District of Columbia' : '';

    let dekText = `At least <span class="highlight">${d3.format(',')(total_cases_this_year)} cases</span> have been reported in ${states_with_cases} states${dcSuffix}.`;

    if (statesWithCasesData.length > 0) {
      const topState = statesWithCasesData.reduce((max, state) => (state.count > max.count ? state : max));
      dekText += ` <span class="highlight">${topState.state_name}</span> has the most cases: <span class="highlight">${d3.format(',')(topState.count)}</span>.`;
    }

    // Update the dek with complete text
    d3.select('.dek').html(dekText);

    // Update the timestamp footnote
    d3.select('#timestamp').html(
      `Note: Current year case figures as of ${updated_date}. Data last checked ${last_updated_str}.`,
    );

    if (window.pymChild) window.pymChild.sendHeight();

    // Continue with map rendering
    console.log('Filtered Measles Data:', filteredMeasles);

    const updatedData = data.map((gridState) => {
      const match = filteredMeasles.find(m => m.abbrev === gridState.key);

      console.log(`Matching ${gridState.key}:`, match); // Debugging

      return match
        ? {
          ...gridState, category: match.category, cases_yn: match.cases_yn || 'No data', count: match.count, state_name: match.state_name,
        }
        : {
          ...gridState, category: 'nocases', cases_yn: 'No reported cases', count: 0, state_name: gridState.key,
        };
    });

    console.log('Final Updated Data:', updatedData); // Debugging

    // Jenks natural breaks classification
    // Adjusted for better contrast between levels
    const colors = ['#E0D4EB', '#C7B2DA', '#AE8FCA', '#8C63B5', '#623E82'];
    const numBins = colors.length;

    // Get all states with cases for classification
    const statesWithCases = updatedData.filter(d => d.count > 0);
    const caseCounts = statesWithCases.map(d => d.count);

    let breaks = [];
    let colorScale;

    if (caseCounts.length > 0 && caseCounts.length >= numBins) {
      // Use ckmeans for natural breaks (similar to Jenks)
      const clusters = ckmeans(caseCounts, numBins);

      // Get the min value of each cluster as break points
      const jenksBreaks = clusters.map(cluster => Math.min(...cluster));

      console.log('Jenks breaks (raw):', jenksBreaks);

      // Round breaks to nice numbers for both display AND color assignment
      breaks = jenksBreaks.map(b => roundToNice(b));

      console.log('Jenks breaks (rounded):', breaks);

      // Create a threshold scale using rounded breaks
      // Domain: [0.5, breaks[1], breaks[2], breaks[3], breaks[4]]
      // Range: [gray for 0, color1 for <breaks[1], color2 for <breaks[2], etc.]
      colorScale = d3.scaleThreshold()
        .domain([0.5, ...breaks.slice(1)]) // 0.5 separates 0 from >0, then remaining breaks
        .range(['#e9e9e9', ...colors]); // Gray for 0, then all 5 colors
    } else if (caseCounts.length > 0) {
      // If fewer states with cases than bins, use quantile
      const extent = d3.extent(caseCounts);
      const numColors = Math.min(caseCounts.length, numBins);
      const rawBreaks = d3.range(0, numColors + 1).map(i => extent[0] + (extent[1] - extent[0]) * i / numColors);

      // Round to nice numbers
      breaks = rawBreaks.map(b => roundToNice(b));

      colorScale = d3.scaleThreshold()
        .domain([0.5, ...breaks.slice(1, -1)])
        .range(['#e9e9e9', ...colors.slice(0, numColors)]);
    } else {
      // No cases - just use gray
      colorScale = () => '#e9e9e9';
      breaks = [];
    }

    // Update data with color based on count
    const finalData = updatedData.map(d => ({
      ...d,
      color: colorScale(d.count),
    }));

    // Get max value for legend
    const maxCaseCount = caseCounts.length > 0 ? d3.max(caseCounts) : 0;

    // breaks are already rounded, use them directly for display
    renderGrid(finalData, colorScale, breaks, breaks, maxCaseCount);

    if (window.pymChild) window.pymChild.sendHeight();
  }).catch(error => console.error('Error loading JSON data:', error));
}
